import { useState } from 'react';
import type { CategoryId, Question, QuizMode, RoadSign } from '../types';
import { QuizRunner } from '../components/QuizRunner';
import type { QuizConfig } from '../components/QuizRunner';
import { AppHeader, DemoNotice } from '../components/common';
import { CATEGORY_BY_ID } from '../data/categories';
import { useT } from '../hooks/useT';
import { selectDaily, selectForLearning, selectMistakes, selectTest, shuffle } from '../services/scheduler';
import { useBank } from '../state/BankContext';
import { useProgress } from '../state/ProgressContext';
import { useSettings } from '../state/SettingsContext';

const TEST_LENGTH = 40;
const TEST_PASS = 30;
const DAILY_LENGTH = 10;
const LEARN_LENGTH = 15;
const SIGN_TEST_LENGTH = 12;

/** Builds a multiple-choice question from a road sign, using other signs' meanings as the wrong choices. */
function buildSignQuestions(signs: RoadSign[], count: number): Question[] {
  if (signs.length < 2) return [];
  const chosen = shuffle(signs).slice(0, Math.min(count, signs.length));

  return chosen.map((sign) => {
    const sameType = signs.filter((s) => s.id !== sign.id && s.type === sign.type);
    const others = signs.filter((s) => s.id !== sign.id && s.type !== sign.type);
    const distractors = [...shuffle(sameType), ...shuffle(others)].slice(0, 3);

    const options = shuffle([sign, ...distractors]);
    const correctIndex = options.findIndex((o) => o.id === sign.id);

    return {
      id: sign.id,
      category: 'road-signs' as CategoryId,
      questionEn: 'What does this sign mean?',
      questionAr: 'ماذا تعني هذه الإشارة؟',
      answersEn: options.map((o) => o.meaningEn),
      answersAr: options.map((o) => o.meaningAr),
      correctIndex,
      image: {
        src: sign.image,
        altEn: `${sign.nameEn} sign`,
        altAr: `إشارة ${sign.nameAr}`,
      },
      explanationEn: `${sign.nameEn}: ${sign.meaningEn}`,
      explanationAr: `${sign.nameAr}: ${sign.meaningAr}`,
      difficulty: 'medium' as const,
      source: sign.source,
      tags: ['sign'],
    };
  });
}

interface QuizScreenProps {
  mode: QuizMode;
  categoryId?: CategoryId;
}

export function QuizScreen({ mode, categoryId }: QuizScreenProps) {
  const t = useT();
  const { uiLang } = useSettings();
  const { questions, signs } = useBank();
  const { progress } = useProgress();

  // Built ONCE when the screen opens, so answering never reshuffles the session.
  const [items] = useState<Question[]>(() => {
    switch (mode) {
      case 'daily':
        return selectDaily(questions, progress, DAILY_LENGTH);
      case 'listen':
        return selectDaily(questions, progress, DAILY_LENGTH);
      case 'learn':
        return selectForLearning(questions, progress, LEARN_LENGTH);
      case 'mistakes':
        return selectMistakes(questions, progress, 20);
      case 'category':
        return selectForLearning(
          questions.filter((q) => q.category === categoryId),
          progress,
          20,
        );
      case 'test':
        return selectTest(questions, TEST_LENGTH);
      case 'signs-test':
        return buildSignQuestions(signs, SIGN_TEST_LENGTH);
      default:
        return [];
    }
  });

  const [testStarted, setTestStarted] = useState(mode !== 'test');

  const config: QuizConfig = {
    immediateFeedback: mode !== 'test',
    allowRetry: mode !== 'test',
    requireSubmit: mode === 'test',
    showExplanation: true,
    autoPlayAll: mode === 'listen',
    passThreshold: mode === 'test' ? Math.min(TEST_PASS, Math.ceil(items.length * 0.75)) : undefined,
    kind: mode === 'signs-test' ? 'sign' : 'question',
    showCounters: mode === 'test',
  };

  const category = categoryId ? CATEGORY_BY_ID[categoryId] : undefined;
  const title =
    mode === 'daily'
      ? t('todayPractice')
      : mode === 'learn'
        ? t('learn')
        : mode === 'test'
          ? t('practiceTest')
          : mode === 'mistakes'
            ? t('myMistakes')
            : mode === 'listen'
              ? t('listenAndAnswer')
              : mode === 'signs-test'
                ? t('testSigns')
                : category
                  ? uiLang === 'ar'
                    ? category.nameAr
                    : category.nameEn
                  : t('learn');

  if (mode === 'test' && !testStarted) {
    return (
      <>
        <AppHeader title={t('practiceTest')} />
        <main id="main" className="app-main stack">
          <section className="card stack">
            <h2 className="card__title">
              <span aria-hidden="true">📝 </span>
              {t('testIntroTitle')}
            </h2>
            <p style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {items.length === TEST_LENGTH
                ? t('testIntroBody')
                : uiLang === 'ar'
                  ? `هذا الاختبار يحتوي على ${items.length} سؤالاً لأن بنك الأسئلة يحتوي على ${items.length} سؤالاً فقط. تحتاج إلى ${config.passThreshold} إجابة صحيحة (75%).`
                  : `This test has ${items.length} questions because the bank currently holds ${items.length}. You need ${config.passThreshold} correct (75%).`}
            </p>
            <button
              type="button"
              className="btn btn--primary btn--big btn--block"
              onClick={() => setTestStarted(true)}
              disabled={items.length === 0}
            >
              {t('startTest')}
            </button>
          </section>
          <DemoNotice />
        </main>
      </>
    );
  }

  return (
    <QuizRunner
      mode={mode}
      title={title}
      subtitle={mode === 'category' && category ? (uiLang === 'ar' ? category.nameAr : category.nameEn) : undefined}
      questions={items}
      config={config}
      categoryId={categoryId}
      emptyMessage={mode === 'mistakes' ? t('noMistakesYet') : t('noQuestionsYet')}
    />
  );
}
